import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { compactNavigation, defaultNavigation, futuristicNavigation, horizontalNavigation } from 'app/mock-api/common/navigation/data';
import { TFMService } from 'app/mock-api/tfm.service';
import { cloneDeep } from 'lodash-es';

@Injectable({providedIn: 'root'})
export class NavigationMockApi
{
    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService, private _tfmService : TFMService)
    {
        // Register Mock API handlers
        this.registerHandlers();
    }

    private _isAdminUser(): boolean
    {
        try
        {
            const override = this._tfmService.getAdminViewOverride();
            if (override === 'admin')
            {
                return true;
            }
            if (override === 'user')
            {
                return false;
            }

            const token = localStorage.getItem('id_token');
            if (!token)
            {
                return false;
            }

            const base64Url = token.split('.')[1];
            if (!base64Url)
            {
                return false;
            }

            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) =>
                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            ).join(''));
            const payload = JSON.parse(jsonPayload);
            return payload?.agt === 'ADMIN';
        }
        catch
        {
            return false;
        }
    }

    private _applyNonAdminRestrictions(items: FuseNavigationItem[]): void
    {
        items.forEach((item) =>
        {
            if (item.id === 'ai-travel')
            {
                item.disabled = true;
            }

            if (item.id === 'destinations' && item.children)
            {
                item.children.forEach((child) =>
                {
                    if (child.id !== 'packages')
                    {
                        child.disabled = true;
                    }
                });
            }

            if (item.id === 'metrics' && item.children)
            {
                item.children.forEach((child) =>
                {
                    child.disabled = true;
                });
            }

            if (item.id === 'resources' && item.children)
            {
                item.children.forEach((child) =>
                {
                    if (child.id === 'report' || child.id === 'setup')
                    {
                        child.disabled = true;
                        child.hidden = () => true;
                    }
                });
            }

            if (item.children)
            {
                this._applyNonAdminRestrictions(item.children);
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void
    {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/common/navigation')
            .reply(() =>
            {
                var agencyId = this._tfmService.getAgencyId();
                var agentId = this._tfmService.getLoggedInUserId();
                const isAdmin = this._isAdminUser();

                if (agencyId == 31) {
                    this._defaultNavigation.forEach((defaultNavItem) =>
                        {
                            if (defaultNavItem.children) {
                                defaultNavItem.children.forEach(child => {
                                    child.disabled = true;
                                    child.hidden = () => true;

                                    if ( child.id === 'dashboard' || child.id === 'dashboard.travel' || child.id === 'app.trips' || child.id === 'cruises' || child.id === 'report'|| child.id === 'setup') {                                        
                                        child.disabled = false;
                                        child.hidden = () => false;
                                    }
                                });
                            }
                        });
                }

                if (agentId == 6) {
                    this._defaultNavigation.forEach((defaultNavItem) =>
                    {
                        if (defaultNavItem.children) { //Enable all menu items
                            defaultNavItem.children.forEach(child => {
                                child.disabled = false;
                            });
                            defaultNavItem.children.forEach(child => {
                                if (child.id === 'ai-travel') {
                                    child.disabled = true;
                                }
                            });
                        }

                        if ( defaultNavItem.id === 'dashboard' && defaultNavItem.children) {
                            defaultNavItem.children.push({
                                id   : 'tasks',
                                title: 'Tasks',
                                type : 'basic',
                                icon : 'heroicons_outline:check-circle',
                                link : '/app/tasks',
                            });
                        }
                    });
                }

                if (!isAdmin) {
                    this._applyNonAdminRestrictions(this._defaultNavigation);
                } else {
                    this._defaultNavigation.forEach((defaultNavItem) =>
                    {
                        if (defaultNavItem.children) {
                            defaultNavItem.children.forEach(child => {
                                if (child.id === 'ai-travel') {
                                    child.disabled = false;
                                }
                            });
                        }
                    });
                }
                
                // Fill compact navigation children using the default navigation
                this._compactNavigation.forEach((compactNavItem) =>
                {
                    this._defaultNavigation.forEach((defaultNavItem) =>
                    {
                        if ( defaultNavItem.id === compactNavItem.id )
                        {
                            compactNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill futuristic navigation children using the default navigation
                this._futuristicNavigation.forEach((futuristicNavItem) =>
                {
                    this._defaultNavigation.forEach((defaultNavItem) =>
                    {
                        if ( defaultNavItem.id === futuristicNavItem.id )
                        {
                            futuristicNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill horizontal navigation children using the default navigation
                this._horizontalNavigation.forEach((horizontalNavItem) =>
                {
                    this._defaultNavigation.forEach((defaultNavItem) =>
                    {
                        if ( defaultNavItem.id === horizontalNavItem.id )
                        {
                            horizontalNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Return the response
                return [
                    200,
                    {
                        compact   : cloneDeep(this._compactNavigation),
                        default   : cloneDeep(this._defaultNavigation),
                        futuristic: cloneDeep(this._futuristicNavigation),
                        horizontal: cloneDeep(this._horizontalNavigation),
                    },
                ];
            });
    }
}
